/**
 * «Мессенджер → Чаты»: чаты с сообщениями за день — клиенты или склеенные
 * группы. Спека `docs/specs/2026-09-25-messenger-chats-design.md`; числа — те
 * же, что у `prokat-runner messenger-chats`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { type ChatRow, DIR_LABEL, loadMessengerChats } from "@/lib/messenger";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "px-3 py-1.5 font-medium";
const select = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
const UNANSWERED = 1; // sMessenger_chat_status «Не отвечено»

export function MessengerChats({ day }: { day: string }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(false);
  const [system, setSystem] = useState("");
  const [account, setAccount] = useState("");
  const [unanswered, setUnanswered] = useState(false);
  const [search, setSearch] = useState("");
  const q = useQuery({ queryKey: ["messenger-chats", day], queryFn: () => loadMessengerChats(day) });

  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Читаю чаты из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить чаты: {String(q.error.message)}</div>;

  const all = groups ? q.data.groups : q.data.clients;
  const systems = [...new Set(all.map((c) => c.system ?? "?"))].sort();
  const accounts = [...new Set(all.flatMap((c) => c.accounts))].sort();
  const needle = search.trim().toLowerCase();
  const rows = all.filter(
    (c: ChatRow) =>
      (!system || (c.system ?? "?") === system) &&
      (!account || c.accounts.includes(account)) &&
      (!unanswered || c.status_id === UNANSWERED) &&
      (!needle || `${c.who ?? ""} ${c.last.text ?? ""}`.toLowerCase().includes(needle)),
  );
  const tabBtn = (on: boolean) =>
    `rounded-[var(--radius-ui)] border border-border px-2.5 py-1 text-[12.5px] ${on ? "bg-bg-hover text-fg" : "text-fg-muted hover:bg-bg-hover"}`;

  return (
    <section className={card} data-annot="чаты">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <button type="button" className={tabBtn(!groups)} onClick={() => setGroups(false)}>
          Клиенты {q.data.clients.length}
        </button>
        <button type="button" className={tabBtn(groups)} onClick={() => setGroups(true)}>
          Группы {q.data.groups.length}
        </button>
        <select value={system} onChange={(e) => setSystem(e.target.value)} className={select}>
          <option value="">все системы</option>
          {systems.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={account} onChange={(e) => setAccount(e.target.value)} className={select}>
          <option value="">все аккаунты</option>
          {accounts.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={unanswered} onChange={(e) => setUnanswered(e.target.checked)} />
          только не отвеченные
        </label>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="собеседник или текст"
          className="h-8 w-56 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg"
        />
        <span className="ml-auto text-[12.5px] text-fg-faint">показано {rows.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Система · аккаунт</th>
              <th className={th}>Собеседник</th>
              <th className={th}>Статус</th>
              <th className={`${th} text-right`}>Вх/исх</th>
              <th className={th}>Отвечали</th>
              <th className={th}>Последнее</th>
              {groups ? null : <th className={th}>Сделка</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/messenger/chat/${c.id}`)}
                className="cursor-pointer border-t border-border align-top hover:bg-bg-hover"
                data-annot={`чат ${c.id}`}
              >
                <td className="whitespace-nowrap px-3 py-1.5">
                  <div>{c.system}</div>
                  <div className="text-[11.5px] text-fg-faint">{c.accounts.join(", ")}</div>
                </td>
                <td className="max-w-[16rem] break-words px-3 py-1.5">{c.who ?? "—"}</td>
                <td className={`whitespace-nowrap px-3 py-1.5 ${c.status_id === UNANSWERED ? "text-err" : "text-fg-muted"}`}>
                  {c.status ?? "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                  {c.counts.in}/{c.counts.out}
                  {c.counts.sender ? <span className="text-fg-faint">/{c.counts.sender}</span> : null}
                </td>
                <td className="px-3 py-1.5 text-fg-muted">{c.users.map((u) => u.name).join(", ") || "—"}</td>
                <td className="max-w-[28rem] px-3 py-1.5">
                  <span className="tabular-nums text-fg-faint">
                    {c.last.at.slice(11, 16)} {DIR_LABEL[c.last.dir]}:
                  </span>{" "}
                  <span className="line-clamp-2">{c.last.text ?? "—"}</span>
                </td>
                {groups ? null : (
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {c.deal ? (
                      <Link
                        to={`/deals/${c.deal.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline"
                        title="Карточка сделки"
                      >
                        {c.deal.id}
                        <span className="ml-1 text-fg-faint">{c.deal.stage}</span>
                      </Link>
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        Сделка — самая свежая по телефону чата (привязку по телефону крон CRM не делает с июля). Группа — копии одного
        чата с разных наших аккаунтов, склеенные в одну строку.
      </div>
    </section>
  );
}
